import ExpirationDateField from "./ExpirationDateField";
import { expirationDatePayload } from "../../utils/expirationDate";
import { purchaseSupplierError } from "./documentPopupMessages";
import { activePurchaseSuppliers } from "../../utils/purchaseSuppliers";
import { itemMatchesSupplier, findItemSupplierRelation } from "../../utils/supplierIdentity";
import { deliveryNoteCorrectionText, deliveryNoteCorrectionError } from "./deliveryNoteCorrectionMessages";
import DeliveryNoteCorrectionControl, { DeliveryNotePendingCorrection, DeliveryNoteQuantityDetail, deliveryNoteStockPreview, correctionUnitSource, validateCorrectionLines } from "./DeliveryNoteCorrections";
import { deliveryNoteDuplicateError } from "./deliveryNoteLifecycleMessages";
import DeliveryNoteAdjustments from "./DeliveryNoteAdjustments";
import { prepareDeliveryNoteAdjustments } from "../../utils/deliveryNoteAdjustments";
import { useReceivingDestinations, deliveryNoteLineLocation, createDestinationLineKey } from "./ReceivingDestinations";
import DeliveryNoteLocationPicker from "./DeliveryNoteLocationPicker";
import TenderDeliveryNoteWarning from "./TenderPriceFeedback";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import {
  buildDeliveryNoteUnitOptions,
  defaultDeliveryNoteUnitLevel,
  deliveryNoteInventoryValues,
  formatUnitNumber,
  isDeliveryNoteUnitLevelValid,
  unitHierarchyLabel,
} from "../../utils/deliveryNoteUnits";
import AddItemPopup from "./AddItemPopup";
import EditDeliveryNotePopup from "./EditDeliveryNotePopup";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import { useComplimentaryConfirmation } from "./ComplimentaryStockConfirmation";
import { usePurchaseQuantityReview } from "./PurchaseQuantityReview";
import ComplimentaryPriceField from "./ComplimentaryPriceField";
import { fetchDeliveryNoteReferencePrice } from "../../utils/deliveryNoteReferencePrice";
import { isDeliveryNotePriceValidForVerification, normalizeComplimentaryPrice, updateComplimentaryLine } from "../../utils/complimentaryStock";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

const styles = `
  .dnp-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
  .dnp-modal{width:min(1420px,97vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:22px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-text);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .dnp-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:16px 20px 12px;border-bottom:1px solid var(--pu-border)}
  .dnp-title{margin:0;font-size:clamp(1.06rem,1.45vw,1.38rem);font-weight:900;line-height:1.05}
  .dnp-sub{margin-top:6px;font-size:.8rem;color:var(--pu-muted)}
  .dnp-badge{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.72rem;font-weight:800;letter-spacing:.08em}
  .dnp-close,.dnp-btn,.dnp-trigger{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text)}
  .dnp-close{min-width:40px;height:40px;padding:0 12px;font-weight:800}
  .dnp-body{padding:12px 20px 20px}
  .dnp-section,.dnp-summary,.dnp-footer,.dnp-card,.dnp-expense,.dnp-meta{border:1px solid var(--pu-border);border-radius:16px;padding:12px 14px;background:var(--pu-soft)}
  .dnp-grid{display:grid;gap:16px}
  .dnp-grid--top{grid-template-columns:minmax(0,1.18fr) minmax(280px,.55fr);align-items:start}
  .dnp-grid--four{grid-template-columns:repeat(4,minmax(0,1fr))}
  .dnp-grid--three{grid-template-columns:repeat(3,minmax(0,1fr))}
  .dnp-grid--two{grid-template-columns:repeat(2,minmax(0,1fr))}
  .dnp-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
  .dnp-help{display:block;margin-top:7px;color:var(--pu-muted);font-size:.78rem;line-height:1.45}
  .dnp-modal .form-control,.dnp-modal .form-select,.dnp-trigger{width:100%;padding:8px 10px;min-height:38px;background:var(--pu-surface)!important;color:var(--pu-text)!important}
  .dnp-modal input[type=number],.dnp-receive input[type=number]{appearance:textfield;-moz-appearance:textfield}
  .dnp-modal input[type=number]::-webkit-outer-spin-button,.dnp-modal input[type=number]::-webkit-inner-spin-button,.dnp-receive input[type=number]::-webkit-outer-spin-button,.dnp-receive input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;appearance:none;margin:0}
  .dnp-modal .form-select option{background:var(--pu-surface);color:var(--pu-text)}
  .dnp-trigger{display:flex;align-items:center;justify-content:space-between;gap:12px;text-align:left}
  .dnp-alert{margin-bottom:16px;border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);border-radius:14px;padding:12px 14px;font-size:.88rem;line-height:1.45}
  .dnp-sectionHead,.dnp-expenseHead,.dnp-footer{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .dnp-sectionHead{margin-bottom:12px}
  .dnp-summary h6,.dnp-sectionHead h6,.dnp-expenseHead h6{margin:0;font-size:.94rem;font-weight:850}
  .dnp-summary p,.dnp-sectionHead p,.dnp-expenseHead p{margin:5px 0 0;color:var(--pu-text);font-size:.76rem;line-height:1.35}
  .dnp-stats{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;margin-top:10px}
  .dnp-stat{border-radius:12px;background:var(--pu-soft);border:1px solid var(--pu-border);padding:8px 10px}
  .dnp-stat span,.dnp-meta span,.dnp-totalBlock span{display:block;color:var(--pu-muted);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
  .dnp-stat strong,.dnp-meta strong{display:block;margin-top:4px;font-size:.92rem;font-weight:800;color:var(--pu-muted)}
  .dnp-totalBlock strong{display:block;margin-top:4px;font-size:1.2rem;font-weight:900;color:var(--pu-text)}
  .dnp-actions,.dnp-inline,.dnp-footerActions,.dnp-expenseActions{display:flex;gap:10px;flex-wrap:wrap;justify-content:flex-end}
  .dnp-btn{padding:9px 13px;font-size:.86rem;font-weight:750}
  .dnp-btn--primary{border-color:var(--pu-primary);background:var(--pu-primary);color:#ffffff}
  .dnp-btn--cyan{border-color:var(--pu-border);color:var(--pu-text);background:var(--pu-soft)}
  .dnp-btn--danger{border-color:var(--pu-danger);color:var(--pu-danger);background:var(--pu-danger-bg)}
  .dnp-btn--soft{border-color:var(--pu-border);background:var(--pu-soft)}
  .dnp-link{padding:0;border:none;background:none;color:var(--pu-text);font-size:.78rem;font-weight:700}
  .dnp-empty{border:1px dashed var(--pu-border);border-radius:20px;padding:24px 22px;background:var(--pu-soft)}
  .dnp-empty strong{display:block;font-size:1rem}
  .dnp-empty span{display:block;margin-top:6px;color:var(--pu-text);font-size:.86rem;line-height:1.45}
  .dnp-shell{border:1px solid var(--pu-border);border-radius:18px;padding:10px;background:var(--pu-soft)}
  .dnp-wrapTable{overflow:auto;border:1px solid var(--pu-border);border-radius:16px;background:var(--pu-soft)}
  .dnp-table{width:100%;min-width:1080px;border-collapse:separate;border-spacing:0;table-layout:fixed;font-size:.8rem}
  .dnp-table th,.dnp-table td{padding:8px 10px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .dnp-table th{position:sticky;top:0;z-index:2;background:var(--pu-primary);color:#ffffff;font-size:.66rem;text-transform:uppercase;letter-spacing:.04em;font-weight:800}
  .dnp-table th:first-child{border-top-left-radius:14px}
  .dnp-table th:last-child{border-top-right-radius:14px}
  .dnp-table tbody tr{background:var(--pu-surface)}
  .dnp-table tbody tr:nth-child(even){background:var(--pu-soft)}
  .dnp-table tbody tr:last-child td{border-bottom:none}
  .dnp-table .form-control{min-height:34px;padding:6px 8px;font-size:.82rem}
  .dnp-table .dnp-trigger{min-height:34px;padding:7px 9px;white-space:normal;align-items:flex-start}
  .dnp-table .dnp-trigger span:first-child{min-width:0;overflow-wrap:anywhere;word-break:break-word;line-height:1.25}
  .dnp-itemMeta{margin-top:6px;color:var(--pu-muted);font-size:.72rem;line-height:1.3;overflow-wrap:anywhere}
  .dnp-unitCell{text-align:center;color:var(--pu-text);font-size:.78rem;font-weight:850;line-height:1.25;overflow-wrap:anywhere}
  .dnp-unitSelect{min-width:112px;text-align:left}
  .dnp-unitRelation{display:block;margin-top:5px;color:var(--pu-muted);font-size:.67rem;font-weight:650;line-height:1.3}
  .dnp-unitPreview{display:block;margin-top:5px;color:var(--pu-text);font-size:.67rem;font-weight:750;line-height:1.3}
  .dnp-input{text-align:center}
  .dnp-total{text-align:right;font-weight:850;color:var(--pu-text)}
  .dnp-mobile{display:grid;gap:12px}
  .dnp-cardTop{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
  .dnp-cardTitle{font-size:.96rem;font-weight:850;color:var(--pu-text);line-height:1.2}
  .dnp-cardSub{margin-top:4px;color:var(--pu-muted);font-size:.78rem;line-height:1.4}
  .dnp-expenses{display:grid;gap:12px}
  .dnp-expense--distributed{border-color:var(--pu-border);background:var(--pu-soft)}
  .dnp-expenseAmount{padding:8px 12px;border-radius:999px;background:var(--pu-soft);border:1px solid var(--pu-border);color:var(--pu-text);font-size:.82rem;font-weight:850}
  .dnp-note{margin-top:12px;border-radius:16px;background:var(--pu-soft);border:1px solid var(--pu-border);padding:12px;color:var(--pu-muted);font-size:.82rem;line-height:1.45}
  .dnp-pickWrap{position:fixed;inset:0;z-index:10040;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .dnp-pick{width:min(720px,94vw);max-height:min(80vh,760px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .dnp-pickHead{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 18px 12px;border-bottom:1px solid var(--pu-border)}
  .dnp-pickHead h6{margin:0;font-size:1rem;font-weight:850}
  .dnp-pickHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem;line-height:1.45}
  .dnp-pickBody{padding:14px 18px 18px}
  .dnp-pickList{display:grid;gap:10px;margin-top:12px}
  .dnp-pickItem{width:100%;text-align:left;border-radius:16px;border:1px solid var(--pu-border);background:var(--pu-soft);padding:12px 14px;color:var(--pu-text)}
  .dnp-pickItem strong{display:block;font-size:.95rem}
  .dnp-pickItem span{display:block;margin-top:4px;font-size:.78rem;color:var(--pu-text);line-height:1.45}
  .dnp-pickEmpty{border:1px dashed var(--pu-border);border-radius:16px;padding:16px;text-align:center;color:var(--pu-text);font-size:.84rem}
  .dnp-poStatus{position:relative;display:flex;min-height:76px;align-items:center;justify-content:center;padding:18px 4px 0}
  .dnp-poText{position:absolute;top:0;right:0;color:var(--pu-text);font-size:.72rem;font-style:italic;font-weight:750;line-height:1;white-space:nowrap}
  .dnp-poText--pending{color:var(--pu-text)}
  .dnp-poText--linked{color:var(--pu-text)}
  .dnp-poStatus .dnp-btn{min-width:108px;justify-content:center}
  .dnp-receiveWrap{position:fixed;inset:0;z-index:10050;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px}
  .dnp-receive{width:min(1120px,calc(100vw - 32px));max-height:calc(100vh - 32px);display:flex;flex-direction:column;overflow:hidden;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .dnp-receiveHead{flex:0 0 auto;display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 20px 12px;border-bottom:1px solid var(--pu-border)}
  .dnp-receiveHead h6{margin:0;font-size:1rem;font-weight:850}
  .dnp-receiveHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem;line-height:1.45}
  .dnp-receiveBody{display:flex;flex-direction:column;gap:14px;min-height:0;overflow:hidden;padding:14px 20px 18px}
  .dnp-receiveTableWrap{min-height:0;overflow:auto;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
  .dnp-receiveTable{width:100%;min-width:780px;border-collapse:separate;border-spacing:0}
  .dnp-receiveTable th,.dnp-receiveTable td{padding:10px 11px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .dnp-receiveTable th:nth-child(2),.dnp-receiveTable td:nth-child(2),.dnp-receiveTable th:nth-child(3),.dnp-receiveTable td:nth-child(3),.dnp-receiveTable th:nth-child(4),.dnp-receiveTable td:nth-child(4){width:112px}
  .dnp-receiveTable th:nth-child(5),.dnp-receiveTable td:nth-child(5){width:132px}
  .dnp-receiveTable th{color:#ffffff;font-size:.7rem;text-transform:uppercase;letter-spacing:.06em}
  .dnp-receiveInput{max-width:104px;margin:0 auto}
  .dnp-receiveMeta{color:var(--pu-muted);font-size:.76rem;line-height:1.35}
  .dnp-receiveFooter{flex:0 0 auto;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
  .dnp-receiveTotal{color:var(--pu-text);font-size:.86rem;font-weight:850}
  @media (max-width:1120px){.dnp-grid--top{grid-template-columns:minmax(0,1fr)}}
  @media (max-width:640px){.dnp-wrap{padding:8px;align-items:flex-start}.dnp-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.dnp-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.dnp-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.dnp-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.dnp-close{min-width:76px;height:36px}.dnp-body{padding:12px 14px 14px}.dnp-grid--four,.dnp-grid--three,.dnp-grid--two,.dnp-stats{grid-template-columns:minmax(0,1fr)}.dnp-sectionHead,.dnp-expenseHead,.dnp-actions,.dnp-inline,.dnp-footer,.dnp-footerActions,.dnp-expenseActions{flex-direction:column;align-items:stretch}.dnp-actions .dnp-btn,.dnp-footerActions .dnp-btn,.dnp-expenseActions .dnp-btn{width:100%}.dnp-pickWrap,.dnp-receiveWrap{padding:10px;align-items:flex-start}.dnp-pick,.dnp-receive{width:100%;max-height:calc(100vh - 20px);border-radius:20px}.dnp-pickHead,.dnp-receiveHead{padding:14px 14px 10px}.dnp-pickBody,.dnp-receiveBody{padding:12px 14px 14px}.dnp-poStatus{min-height:auto;padding-top:18px}.dnp-receiveTable{min-width:720px}}
`;

const today = () => new Date().toISOString().slice(0, 10);
const initialForm = () => ({ supplier_id: "", delivery_note_number: "", delivery_note_date: today(), destination_location_id: "", document_type: "standard", correction_manual_pending: false, lines: [], expenses: [] });
const blankLine = () => ({ _destinationKey: createDestinationLineKey(), item_id: "", unit_level: "unit", quantity_in_dn: "", unit_price_in_dn: "", expiration_date: null, total_price_in_dn: 0, is_free_stock: false, free_stock_reason: "", po_receipts: [] });
const sortByName = (list = []) => [...list].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "en", { sensitivity: "base" }));
const money = (value) => (Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)} EUR` : "-");
const qty = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return formatUnitNumber(n, 8);
};
const withTotal = (sourceLine) => {
  const line = normalizeComplimentaryPrice(sourceLine);
  const q = Number(line?.quantity_in_dn || 0);
  const p = line?.is_free_stock ? 0 : Number(line?.unit_price_in_dn || 0);
  return {
    ...line,
    unit_price_in_dn: line?.is_free_stock ? "0" : line?.unit_price_in_dn,
    total_price_in_dn: Number.isFinite(q * p) ? q * p : 0,
  };
};
const totalOf = (lines = [], expenses = []) =>
  lines.reduce((sum, line) => sum + (Number(withTotal(line).total_price_in_dn) || 0) * (Number(line.correction_direction) === -1 ? -1 : 1), 0) +
  expenses.filter((expense) => !expense?.distribute_cost).reduce((sum, expense) => sum + (Number(expense?.amount) || 0), 0);
const sessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "null") || {};
  } catch (error) {
    console.warn("[AddDeliveryNotePopup] Could not parse palantir_user", error);
    return {};
  }
};
const businessUnitId = () => sessionUser()?.active_business_unit_id || null;
const canVerifyDeliveryNote = () => {
  const user = sessionUser();
  const permissions = user?.permissions || {};
  const isAdminRole = Number(user?.id) === 1;
  return isAdminRole || Boolean(permissions.access_settings) || Boolean(permissions["purchasing.delivery_notes.verify"]);
};
const supplierLabel = (supplier) => supplier ? (supplier.supplier_id ? `${supplier.supplier_id} - ${supplier.name}` : supplier.name) : "Select supplier";
const itemLabel = (item) => item ? (item.code ? `${item.code} - ${item.name}` : item.name) : "Select item";
const relationFor = findItemSupplierRelation;
const suggestedPrice = (item, supplierId) => {
  const relation = relationFor(item, supplierId);
  const price = relation?.purchase_price ?? relation?.price ?? item?.unit_price ?? item?.purchase_price ?? item?.price;
  return price === undefined || price === null || Number.isNaN(Number(price)) ? "" : String(price);
};
const packaging = (item, unitMap, languageId) => unitHierarchyLabel(item, unitMap, languageId);
const assignmentQty = (line) =>
  (line?.po_receipts || []).reduce((sum, receipt) => sum + (Number(receipt?.quantity_received ?? receipt?.quantity) || 0), 0);
const pendingQtyFromPo = (line) => Number(line?.pending_quantity ?? line?.quantity_pending ?? 0) || 0;
const poLineLabel = (line) => line?.order?.order_code || line?.order_code || `PO line ${line?.id || line?.po_line_id || ""}`;
const poDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString("en-GB");
};

function buildInitialReceiveQuantities(line, item, candidates, unitMap) {
  const existing = new Map((line?.po_receipts || []).map((receipt) => [String(receipt.po_line_id), String(receipt.quantity_received ?? "")]));
  if (existing.size) return Object.fromEntries(existing);

  let remaining = deliveryNoteInventoryValues(line, item, unitMap).quantity || 0;
  return candidates.reduce((acc, candidate) => {
    const pending = pendingQtyFromPo(candidate);
    const nextQty = Math.max(Math.min(remaining, pending), 0);
    if (nextQty > 0) {
      acc[String(candidate.id || candidate.po_line_id)] = String(nextQty);
      remaining -= nextQty;
    }
    return acc;
  }, {});
}

function PickerModal({ open, title, subtitle, query, onQuery, onClose, children }) {
  const { themeId, t } = useReceivingPopupUi();
  if (!open) return null;
  return (
    <div className="dnp-pickWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dnp-pick" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dnp-pickHead">
          <div>
            <h6>{t(title)}</h6>
            <p>{t(subtitle)}</p>
          </div>
          <button type="button" className="dnp-btn dnp-btn--soft" onClick={onClose}> {t("Close")} </button>
        </div>
        <div className="dnp-pickBody">
          <input
            autoFocus
            className="form-control"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder={t("Search by code, name or supplier reference...")}
          />
          <div className="dnp-pickList">{children}</div>
        </div>
      </div>
    </div>
  );
}

function ReceivePoModal({ open, line, item, unitMap, candidates, quantities, onQuantity, onClose, onApply }) {
  const { themeId, t, languageId } = useReceivingPopupUi();
  if (!open) return null;
  const total = Object.values(quantities || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const inventoryValues = deliveryNoteInventoryValues(line, item, unitMap, languageId);
  const lineQuantity = inventoryValues.quantity || 0;
  const inventoryUnit = inventoryValues.option?.inventoryLabel || "inventory units";

  return (
    <div className="dnp-receiveWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dnp-receive" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dnp-receiveHead">
          <div>
            <h6>{t("Receive against pending POs")}</h6>
            <p>{item ? t(itemLabel(item)) : t("Selected delivery note line")} {t("· Available")} {qty(lineQuantity)} {inventoryUnit}</p>
          </div>
          <button type="button" className="dnp-btn dnp-btn--soft" onClick={onClose}> {t("Close")} </button>
        </div>
        <div className="dnp-receiveBody">
          {candidates.length ? (
            <div className="dnp-receiveTableWrap">
              <table className="dnp-receiveTable">
                <thead>
                  <tr>
                    <th>{t("PO")}</th>
                    <th style={{ textAlign: "center" }}>{t("Expected")}</th>
                    <th style={{ textAlign: "center" }}>{t("Ordered")}</th>
                    <th style={{ textAlign: "center" }}>{t("Pending")}</th>
                    <th style={{ textAlign: "center" }}>{t("Receive")}</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate) => {
                    const poLineId = String(candidate.id || candidate.po_line_id);
                    return (
                      <tr key={`receive-po-${poLineId}`}>
                        <td>
                          <strong>{poLineLabel(candidate)}</strong>
                          <div className="dnp-receiveMeta"> {t("Unit price")} {money(candidate.unit_price)} {t("· Line #")}{candidate.id || candidate.po_line_id}
                          </div>
                        </td>
                        <td style={{ textAlign: "center" }}>{poDate(candidate.order?.expected_delivery_date || candidate.expected_delivery_date)}</td>
                        <td style={{ textAlign: "center" }}>{qty(candidate.quantity || candidate.ordered_quantity)}</td>
                        <td style={{ textAlign: "center" }}>{qty(pendingQtyFromPo(candidate))}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.00000001"
                            className="form-control dnp-input dnp-receiveInput"
                            value={quantities?.[poLineId] || ""}
                            onChange={(event) => onQuantity(poLineId, event.target.value)}
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="dnp-empty">
              <strong>{t("No pending purchase orders")}</strong>
              <span>{t("This supplier and item do not currently have pending PO quantities.")}</span>
            </div>
          )}

          <div className="dnp-receiveFooter">
            <div className="dnp-receiveTotal"> {t("Assigned")} {qty(total)} / {qty(lineQuantity)} {inventoryUnit}
            </div>
            <div className="dnp-footerActions">
              <button type="button" className="dnp-btn dnp-btn--soft" onClick={onClose}> {t("Cancel")} </button>
              <button type="button" className="dnp-btn dnp-btn--primary" onClick={onApply} disabled={!candidates.length}> {t("Apply")} </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AddDeliveryNotePopup({ isOpen, onClose, onAdded }) {
  const { t, themeId, languageId } = useReceivingPopupUi();
  const saveInProgress = useRef(false);
  const isMobile = useIsMobile();
  const linesRef = useRef(null);
  const tableRef = useRef(null);
  const [form, setForm] = useState(initialForm);
  const activeBusinessUnitId = businessUnitId();
  const isCorrection = form.document_type === "rectification";
  const complimentary = useComplimentaryConfirmation({ isActive: isOpen,
    scopeKey: JSON.stringify([activeBusinessUnitId, form.supplier_id, form.delivery_note_date]) });
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingMode, setSavingMode] = useState("");
  const [error, setError] = useState("");
  const [supplierPicker, setSupplierPicker] = useState({ open: false, query: "" });
  const [itemPicker, setItemPicker] = useState({ open: false, lineIndex: null, query: "" });
  const [showItemPopup, setShowItemPopup] = useState(false);
  const [pendingLineIndex, setPendingLineIndex] = useState(null);
  const [pendingPoLines, setPendingPoLines] = useState([]);
  const [loadingPendingPo, setLoadingPendingPo] = useState(false);
  const [receiveModal, setReceiveModal] = useState({ open: false, lineIndex: null, quantities: {} });
  const [createdDraft, setCreatedDraft] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setForm(initialForm());
      setSuppliers([]);
      setItems([]);
      setUnits([]);
      setInventoryLocations([]);
      setLoading(false);
      setSavingMode("");
      setError("");
      setSupplierPicker({ open: false, query: "" });
      setItemPicker({ open: false, lineIndex: null, query: "" });
      setShowItemPopup(false);
      setPendingLineIndex(null);
      setPendingPoLines([]);
      setLoadingPendingPo(false);
      setReceiveModal({ open: false, lineIndex: null, quantities: {} });
      setCreatedDraft(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const bu = businessUnitId();
        if (!bu) throw new Error("No active business unit detected");
        const [supRes, itemRes, unitRes, locationsRes] = await Promise.all([
          axios.get(`/api/purchasing/suppliers/options?business_unit_id=${bu}`),
          axios.get("/api/purchasing/items"),
          axios.get(`/api/purchasing/units?business_unit_id=${bu}`),
          axios.get("/api/purchasing/inventory/locations?include_inactive=false"),
        ]);
        if (cancelled) return;
        setSuppliers(sortByName(activePurchaseSuppliers(supRes.data)));
        setItems(sortByName(itemRes.data || []));
        setUnits(sortByName(unitRes.data || []));
        setInventoryLocations(Array.isArray(locationsRes.data) ? locationsRes.data : []);
      } catch (requestError) {
        console.error("[AddDeliveryNotePopup] Error loading base data:", requestError);
        if (!cancelled) setError("Could not load suppliers or items for this delivery note.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (pendingLineIndex !== null && pendingLineIndex >= form.lines.length) setPendingLineIndex(null);
    if (itemPicker.lineIndex !== null && itemPicker.lineIndex >= form.lines.length) {
      setItemPicker({ open: false, lineIndex: null, query: "" });
    }
  }, [form.lines, itemPicker.lineIndex, pendingLineIndex]);

  const unitMap = useMemo(() => new Map(units.map((unit) => [String(unit.id), unit])), [units]);
  const itemMap = useMemo(() => new Map(items.map((item) => [String(item.id), item])), [items]);
  const selectedSupplier = useMemo(() => suppliers.find((supplier) => String(supplier.id) === String(form.supplier_id)) || null, [suppliers, form.supplier_id]);
  const supplierItems = useMemo(() => sortByName(items.filter((item) => !form.supplier_id || itemMatchesSupplier(item, selectedSupplier || form.supplier_id))), [items, selectedSupplier, form.supplier_id]);
  const filteredSuppliers = useMemo(() => {
    const query = supplierPicker.query.trim().toLowerCase();
    if (!query) return suppliers;
    return suppliers.filter((supplier) =>
      [supplier.name, supplier.supplier_id, supplier.code]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [supplierPicker.query, suppliers]);
  const filteredItems = useMemo(() => {
    const query = itemPicker.query.trim().toLowerCase();
    if (!query) return supplierItems;
    return supplierItems.filter((item) => {
      const relation = relationFor(item, selectedSupplier || form.supplier_id);
      return [item.name, item.code, relation?.supplier_code, relation?.code, relation?.reference]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [form.supplier_id, selectedSupplier, itemPicker.query, supplierItems]);
  const lines = useMemo(() => form.lines.map((line) => withTotal(line)), [form.lines]);
  const quantityReview = usePurchaseQuantityReview({
    documentType: "delivery_note", documentId: null,
    currency: form.currency || "EUR",
    date: form.delivery_note_date, lines, enabled: isOpen && !createdDraft && !isCorrection,
  });
  const total = useMemo(() => totalOf(lines, form.expenses), [form.expenses, lines]);
  const hasPendingPrices = lines.some((line) => line.price_status === "missing" || line.price_status === "estimated");
  const canSaveAndVerify = canVerifyDeliveryNote();
  const saving = Boolean(savingMode);
  const pendingByItem = useMemo(() => {
    const grouped = new Map();
    pendingPoLines.forEach((line) => {
      const pending = pendingQtyFromPo(line);
      if (!Number.isFinite(pending) || pending <= 0) return;
      const key = String(line.item_id);
      grouped.set(key, [...(grouped.get(key) || []), line]);
    });
    return grouped;
  }, [pendingPoLines]);

  useEffect(() => {
    if (!isOpen || !form.supplier_id || isCorrection) {
      setPendingPoLines([]);
      return;
    }

    const itemIds = [...new Set(form.lines.map((line) => line.item_id).filter(Boolean).map(String))];
    if (!itemIds.length) {
      setPendingPoLines([]);
      return;
    }

    let cancelled = false;
    const loadPendingPoLines = async () => {
      setLoadingPendingPo(true);
      try {
        const response = await axios.get("/api/purchasing/delivery-notes-direct/pending-po-lines", {
          params: { supplier_id: form.supplier_id, item_ids: itemIds.join(",") },
        });
        if (!cancelled) setPendingPoLines(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        console.error("[AddDeliveryNotePopup] Error loading pending PO lines:", requestError);
        if (!cancelled) setPendingPoLines([]);
      } finally {
        if (!cancelled) setLoadingPendingPo(false);
      }
    };
    loadPendingPoLines();
    return () => {
      cancelled = true;
    };
  }, [form.lines, form.supplier_id, isOpen, isCorrection]);

  const setField = (field, value) => {
    setForm((prev) => {
      if (field === "supplier_id" && String(prev.supplier_id || "") !== String(value || "")) {
        return {
          ...prev,
          [field]: value,
          document_type: "standard", original_delivery_note_id: null, original_delivery_note_number: null,
          lines: prev.lines.map((line) => ({ ...line, item_id: "", original_delivery_note_line_id: null, correction_direction: null, correction_available_quantity: null, received_quantity: null, inventory_quantity_snapshot: null, destination_location_id: null, destination_location_source: null, unit_level: "unit", unit_price_in_dn: "", expiration_date: null, is_free_stock: false, free_stock_reason: "", po_receipts: [] })),
        };
      }
      return { ...prev, [field]: value };
    });
    setError("");
  };

  const setLineField = (index, field, value) => {
    if (["unit_price_in_dn", "item_id", "quantity_in_dn", "unit_level"].includes(field)) complimentary.forgetLine(form.lines[index], index);
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === index
          ? withTotal({
              ...updateComplimentaryLine(line, field, value, {
                priceKey: "unit_price_in_dn",
                identityFields: ["item_id", "quantity_in_dn", "unit_level"],
              }),
              ...(field === "unit_level" && line.unit_level !== value ? { po_receipts: [] } : {}),
            })
          : line
      ),
    }));
    setError("");
  };

  const getReferencePrice = (line) => fetchDeliveryNoteReferencePrice({ line,
    item: isCorrection ? correctionUnitSource(line, itemMap.get(String(line.item_id))) : itemMap.get(String(line.item_id)), businessUnitId: activeBusinessUnitId,
    supplierId: form.supplier_id, date: form.delivery_note_date, unitMap, languageId });

  const editLinePrice = async (index) => {
    const source = form.lines[index];
    const prepared = await complimentary.editLine(source, {
      getLastPrice: getReferencePrice,
      getItemLabel: (line) => itemLabel(itemMap.get(String(line.item_id))),
      getUnitLabel: (line) => buildDeliveryNoteUnitOptions(itemMap.get(String(line.item_id)), unitMap, languageId)
        .find((option) => option.level === line.unit_level)?.label || line.unit_level,
    });
    if (!prepared) return;
    setForm((previous) => ({ ...previous, lines: previous.lines.map((line) => line === source ? withTotal(prepared) : line) }));
    setError("");
  };

  const addLine = () => {
    if (!form.supplier_id) {
      setError(t("Select a supplier before adding items to this delivery note."));
      return;
    }
    setForm((prev) => ({ ...prev, lines: [blankLine(), ...prev.lines] }));
    setError("");
    window.setTimeout(() => {
      tableRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const modal = linesRef.current?.closest(".dnp-modal");
      if (modal && linesRef.current) {
        modal.scrollTo({ top: Math.max(linesRef.current.offsetTop - 18, 0), behavior: "smooth" });
      }
      if (!isMobile) setItemPicker({ open: true, lineIndex: 0, query: "" });
    }, 0);
  };

  const removeLine = (index) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));
    setItemPicker((prev) => {
      if (prev.lineIndex === null) return prev;
      if (prev.lineIndex === index) return { open: false, lineIndex: null, query: "" };
      if (prev.lineIndex > index) return { ...prev, lineIndex: prev.lineIndex - 1 };
      return prev;
    });
  };

  const pickItem = (index, item) => {
    if (index === null || index === undefined || !item) return;
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) => {
        if (i !== index) return line;
        const sameItem = String(line.item_id || "") === String(item.id || "");
        const nextUnitLevel = sameItem && isDeliveryNoteUnitLevelValid(item, line.unit_level, unitMap)
          ? line.unit_level
          : defaultDeliveryNoteUnitLevel(item, { unitMap });
        const nextLine = updateComplimentaryLine(updateComplimentaryLine(line, "item_id", item.id, {
          priceKey: "unit_price_in_dn", identityFields: ["item_id"],
        }), "unit_level", nextUnitLevel, { priceKey: "unit_price_in_dn", identityFields: ["unit_level"] });
        return withTotal({
          ...nextLine,
          ...(!sameItem ? { destination_location_id: null, destination_location_source: null, expiration_date: null } : {}),
          unit_level: nextUnitLevel,
          po_receipts: sameItem ? line.po_receipts || [] : [],
          unit_price_in_dn: line.unit_price_in_dn || suggestedPrice(item, suppliers.find((supplier) => Number(supplier.id) === Number(prev.supplier_id)) || prev.supplier_id),
        });
      }),
    }));
    setItemPicker({ open: false, lineIndex: null, query: "" });
    setError("");
  };

  const openReceiveModal = (index) => {
    const line = lines[index];
    if (!line?.item_id) {
      setError(t("Select an item before receiving it against pending purchase orders."));
      return;
    }
    const candidates = pendingByItem.get(String(line.item_id)) || [];
    if (loadingPendingPo || !candidates.length) return;
    setReceiveModal({
      open: true,
      lineIndex: index,
      quantities: buildInitialReceiveQuantities(line, itemMap.get(String(line.item_id)), candidates, unitMap),
    });
    setError("");
  };

  const setReceiveQuantity = (poLineId, value) => {
    setReceiveModal((prev) => ({
      ...prev,
      quantities: { ...(prev.quantities || {}), [String(poLineId)]: value },
    }));
  };

  const applyReceiveAssignments = () => {
    const index = receiveModal.lineIndex;
    const line = lines[index];
    if (index === null || index === undefined || !line) return;

    const candidates = pendingByItem.get(String(line.item_id)) || [];
    const candidateMap = new Map(candidates.map((candidate) => [String(candidate.id || candidate.po_line_id), candidate]));
    const assignments = Object.entries(receiveModal.quantities || {})
      .map(([poLineId, value]) => ({
        po_line_id: Number(poLineId),
        quantity_received: Number(value),
      }))
      .filter((assignment) => Number.isFinite(assignment.quantity_received) && assignment.quantity_received > 0);

    const lineQuantity = deliveryNoteInventoryValues(
      line,
      itemMap.get(String(line.item_id)),
      unitMap
    ).quantity || 0;
    const totalAssigned = assignments.reduce((sum, assignment) => sum + assignment.quantity_received, 0);
    if (lineQuantity > 0 && totalAssigned > lineQuantity + 0.0001) {
      setError(t("The quantity assigned to purchase orders cannot exceed the delivery note quantity converted to the item's main unit."));
      return;
    }

    for (const assignment of assignments) {
      const candidate = candidateMap.get(String(assignment.po_line_id));
      const pending = pendingQtyFromPo(candidate);
      if (!candidate || assignment.quantity_received > pending + 0.0001) {
        setError(t("One of the assigned quantities is higher than the pending PO quantity."));
        return;
      }
    }

    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((candidateLine, lineIndex) =>
        lineIndex === index ? { ...candidateLine, po_receipts: assignments } : candidateLine
      ),
    }));
    setReceiveModal({ open: false, lineIndex: null, quantities: {} });
    setError("");
  };

  const openCreateItem = (index) => {
    setPendingLineIndex(index);
    setItemPicker({ open: false, lineIndex: null, query: "" });
    setShowItemPopup(true);
  };

  const save = async ({ verify = false } = {}) => {
    if (saveInProgress.current) return;
    if (!form.supplier_id || !String(form.delivery_note_number || "").trim()) {
      setError(t("Select a supplier and enter the supplier delivery note number."));
      return;
    }
    if (verify && !canSaveAndVerify) {
      setError(t("You do not have permission to verify delivery notes."));
      return;
    }
    if (verify && !isCorrection && lines.some((line) => line.item_id && !deliveryNoteLineLocation(line, form.destination_location_id,
      destinationSuggestions[line.item_id], { pendingPoLines }))) {
      setError(t("Select a destination for each unresolved item before verifying the delivery note."));
      setLocationRequest((previous) => previous + 1);
      return;
    }
    saveInProgress.current = true;
    setSavingMode(verify ? "verify" : "save");
    setError("");
    let createdResult;
    try {
      let cleanedLines = lines.filter((line) => line.item_id || line.quantity_in_dn || line.unit_price_in_dn);
      if (isCorrection) {
        const correctionError = validateCorrectionLines(cleanedLines.map((line) => correctionUnitSource(line, itemMap.get(String(line.item_id)))), languageId);
        if (correctionError) { setError(correctionError); return; }
      }
      const invalidUnitLine = cleanedLines.find((line) => {
        const item = isCorrection ? correctionUnitSource(line, itemMap.get(String(line.item_id))) : itemMap.get(String(line.item_id));
        return !item || !isDeliveryNoteUnitLevelValid(item, line.unit_level, unitMap);
      });
      if (invalidUnitLine) {
        setError(t("Every delivery note line must have a valid unit selected."));
        return;
      }
      cleanedLines = await complimentary.confirmLines(cleanedLines, {
        getLastPrice: getReferencePrice,
        priceKey: "unit_price_in_dn",
        quantityKey: "quantity_in_dn",
        includeBlank: verify,
        getItemLabel: (line) => itemLabel(itemMap.get(String(line.item_id))),
        getUnitLabel: (line) => buildDeliveryNoteUnitOptions(itemMap.get(String(line.item_id)), unitMap, languageId)
          .find((option) => option.level === line.unit_level)?.label || line.unit_level,
      });
      if (!cleanedLines) return;
      let quantityReviewToken;
      if (verify) {
        const invalidCommercialLine = cleanedLines.find(
          (line) =>
            !(Number(line.quantity_in_dn) > 0) ||
            !isDeliveryNotePriceValidForVerification(line)
        );
        if (invalidCommercialLine) {
          setError(t("Every line needs a positive quantity and a valid price, or an explicit Complimentary or Missing choice."));
          return;
        }
        setForm((previous) => ({ ...previous, lines: cleanedLines }));
        const reviewResult = isCorrection ? { allowed: true } : await quantityReview.check({ lines: cleanedLines });
        if (!reviewResult.allowed) return;
        quantityReviewToken = reviewResult.token;
      }
      const cleanedExpenses = prepareDeliveryNoteAdjustments(form.expenses, cleanedLines);
      const payload = {
        ...form,
        delivery_note_number: String(form.delivery_note_number || "").trim(),
        lines: cleanedLines.map((line) => ({ ...line, expiration_date: expirationDatePayload(line, itemMap.get(String(line.item_id))) })),
        expenses: cleanedExpenses,
        total_amount: totalOf(cleanedLines, cleanedExpenses),
        status: "draft",
      };
      setForm((previous) => ({ ...previous, lines: cleanedLines }));
      const response = await axios.post("/api/purchasing/delivery-notes-direct", payload);
      const deliveryNoteId = response.data?.delivery_note?.id || response.data?.id;
      if (deliveryNoteId) createdResult = { id: deliveryNoteId, response: response.data };
      if (verify) {
        if (!deliveryNoteId) throw new Error("Missing delivery note id after creation");
        await axios.post(`/api/purchasing/receivings/delivery-notes/${deliveryNoteId}/verify`, {
          ...(quantityReviewToken ? { quantity_review_token: quantityReviewToken, quantity_review_acknowledged: true } : {}),
        });
      }
      if (typeof onAdded === "function") onAdded(response.data);
      onClose();
    } catch (requestError) {
      if (createdResult) {
        setCreatedDraft({ ...createdResult, error: requestError });
        return;
      }
      if (quantityReview.handleError(requestError)) return;
      console.error("[AddDeliveryNotePopup] Error creating delivery note:", requestError);
      setError(
        purchaseSupplierError(requestError, languageId) || deliveryNoteCorrectionError(requestError, languageId) || deliveryNoteDuplicateError(requestError, languageId) || requestError.response?.data?.error || requestError.message || (verify
          ? t("Could not create and verify this delivery note. Please review the data and try again.")
          : t("Could not create this delivery note. Please review the data and try again."))
      );
    } finally {
      saveInProgress.current = false;
      setSavingMode("");
    }
  };

  const handleItemAdded = async (newItem) => {
    try {
      const refreshed = await axios.get(`/api/purchasing/items/${newItem.id}`);
      const enriched = refreshed.data;
      if (form.supplier_id && !enriched.item_suppliers?.length) {
        enriched.item_suppliers = [{ supplier_id: Number(form.supplier_id) }];
      }
      setItems((prev) => sortByName([enriched, ...prev]));
      if (pendingLineIndex !== null) pickItem(pendingLineIndex, enriched);
    } catch (requestError) {
      console.error("[AddDeliveryNotePopup] Error refreshing new item:", requestError);
      setItems((prev) => sortByName([newItem, ...prev]));
      if (pendingLineIndex !== null) pickItem(pendingLineIndex, newItem);
    } finally {
      setPendingLineIndex(null);
      setShowItemPopup(false);
    }
  };

  const [locationRequest, setLocationRequest] = useState(0);
  useEffect(() => { setLocationRequest(0); }, [isOpen]);
  const changeLineDestination = (index, destination, source) => {
    setForm((previous) => ({ ...previous, lines: previous.lines.map((line, i) => i === index ? { ...line, destination_location_id: destination, destination_location_source: source } : line) }));
  };
  const { suggestions: destinationSuggestions } = useReceivingDestinations({
    lines: form?.lines || [], items, locations: inventoryLocations,
    headerLocationId: form?.destination_location_id, scopeKey: form.supplier_id, disabled: !isOpen || Boolean(createdDraft) || isCorrection,
    pendingPoLines,
    requireAssignedDestination: true, onChange: changeLineDestination,
  });
  const destinationLocations = Object.values(destinationSuggestions)[0]?.locations || inventoryLocations;
  const renderLineLocation = (line, index) => !isCorrection && line.item_id ? <DeliveryNoteLocationPicker compact
    value={deliveryNoteLineLocation(line, form.destination_location_id, destinationSuggestions[line.item_id], { pendingPoLines }) || ""}
    automatic={!line.destination_location_id && !form.destination_location_id}
    generalLocationId={form.destination_location_id}
    locations={destinationSuggestions[line.item_id]?.locations || inventoryLocations}
    label={`${t("Destination Location")}: ${itemLabel(itemMap.get(String(line.item_id)))}`}
    disabled={Boolean(savingMode)} onChange={(destination, source) => changeLineDestination(index, destination, source)} /> : null;

  if (!isOpen) return null;
  if (createdDraft) return <EditDeliveryNotePopup isOpen deliveryNoteId={createdDraft.id}
    initialVerificationError={createdDraft.error}
    onClose={(result) => {
      onAdded?.(createdDraft.response);
      onClose?.(result);
    }} />;

  const blockWhileSaving = (event) => {
    if (saveInProgress.current || complimentary.isOpen) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <>
    <div className="dnp-wrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
      aria-hidden={complimentary.isOpen || undefined}
      onClickCapture={blockWhileSaving} onChangeCapture={blockWhileSaving} onKeyDownCapture={blockWhileSaving}>
      <style>{styles}</style>
      <div className="dnp-modal" role="dialog" aria-modal="true" aria-label={t("New Delivery Note")}>
        <div className="dnp-head">
          <div>
            <h2 className="dnp-title">{t("New Delivery Note")}</h2>
            <div className="dnp-sub">
              {selectedSupplier
                ? `${t(supplierLabel(selectedSupplier))} · ${form.delivery_note_number || t("Draft pending supplier note number")}`
                : t("Direct supplier note draft")}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="dnp-badge">{t("DIRECT")}</span>
            <button type="button" className="dnp-close" onClick={onClose} disabled={saving || complimentary.isOpen}>
              {isMobile ? t("Close") : t("X")}
            </button>
          </div>
        </div>

        <div className="dnp-body">
          {error ? <div className="dnp-alert">{t(error)}</div> : null}

          <div className="dnp-grid dnp-grid--top">
            <section className="dnp-section">
              <div className="dnp-grid dnp-grid--four">
                <div>
                  <label className="dnp-label">{t("Supplier")}</label>
                  {isMobile ? (
                    <select
                      className="form-select"
                      value={form.supplier_id}
                      onChange={(e) => setField("supplier_id", e.target.value)}
                      disabled={loading}
                    >
                      <option value="">{t("Select supplier")}</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {t(supplierLabel(supplier))}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      className="dnp-trigger"
                      onClick={() => setSupplierPicker({ open: true, query: "" })}
                      disabled={loading}
                    >
                      <span>{selectedSupplier ? t(supplierLabel(selectedSupplier)) : t("Select supplier")}</span>
                      <span>{t("Browse")}</span>
                    </button>
                  )}
                  <span className="dnp-help">{t("Pick the supplier first so the item list becomes shorter.")}</span>
                </div>

                <div>
                  <span className="dnp-label">{deliveryNoteCorrectionText(languageId, "Delivery note type")}</span>
                  <DeliveryNoteCorrectionControl note={form} businessUnitId={activeBusinessUnitId} disabled={loading || saving}
                    onClear={() => setForm((previous) => ({ ...previous, document_type: "standard", original_delivery_note_id: null, original_delivery_note_number: null, lines: previous.lines.map((line) => ({ ...line, original_delivery_note_line_id: null, correction_direction: null, correction_available_quantity: null, received_quantity: null, inventory_quantity_snapshot: null })) }))}
                    onSelect={(original, selectedLines) => {
                      setItems((previous) => [...previous, ...selectedLines.filter((line) => !previous.some((item) => String(item.id) === String(line.item_id))).map((line) => ({ ...line, id: line.item_id, name: line.item_name || line.description }))]);
                      setForm((previous) => ({ ...previous, document_type: "rectification", correction_manual_pending: false,
                        original_delivery_note_id: original.id, original_delivery_note_number: original.delivery_note_number, expenses: [],
                        lines: selectedLines.map((line) => ({ ...line, _destinationKey: createDestinationLineKey() })) }));
                    }} />
                </div>

                <div>
                  <label className="dnp-label">{t("Delivery Note Date")}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.delivery_note_date}
                    onChange={(e) => setField("delivery_note_date", e.target.value)}
                  />
                  <span className="dnp-help">{t("The date shown on the delivery note itself.")}</span>
                </div>

                <div>
                  <label className="dnp-label">{t("Destination Location")}</label>
                  <DeliveryNoteLocationPicker
                    requestOpen={locationRequest} locations={destinationLocations} label={t("Destination Location")}
                    value={form.destination_location_id || ""}
                    onChange={(destination) => setField("destination_location_id", destination)}
                    disabled={loading || Boolean(savingMode) || isCorrection}
                  />
                </div>
                <div>
                  <label className="dnp-label">{t("Delivery Note Number")}</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("Supplier note number")}
                    value={form.delivery_note_number}
                    onChange={(e) => setField("delivery_note_number", e.target.value)}
                  />
                  <span className="dnp-help">{t("Use the number printed on the supplier document.")}</span>
                </div>

              </div>
            </section>

            <aside className="dnp-summary">
              <h6>{selectedSupplier ? t("Ready to capture the note") : t("Choose the supplier to start")}</h6>
              <p>
                {selectedSupplier
                  ? t("{count} matching items are ready for {supplier}.", { count: supplierItems.length, supplier: selectedSupplier.name })
                  : t("Once the supplier is selected, the item picker becomes much easier to use.")}
              </p>
              <div className="dnp-stats">
                <div className="dnp-stat">
                  <span>{t("Lines")}</span>
                  <strong>{lines.length}</strong>
                </div>
                <div className="dnp-stat">
                  <span>{t("Expenses")}</span>
                  <strong>{form.expenses.length}</strong>
                </div>
                <div className="dnp-stat">
                  <span>{t("Supplier Items")}</span>
                  <strong>{selectedSupplier ? supplierItems.length : "-"}</strong>
                </div>
                <div className="dnp-stat">
                  <span>{t(hasPendingPrices ? "Provisional total" : "Total")}</span>
                  <strong>{money(total)}</strong>
                </div>
              </div>
            </aside>
          </div>

          <TenderDeliveryNoteWarning isOpen={isOpen} supplierId={form.supplier_id} date={form.delivery_note_date} lines={form.lines} />
          <section className="dnp-section mt-3" ref={linesRef}>
            <div className="dnp-sectionHead">
              <div>
                <h6>{t("Delivery Note Lines")}</h6>
                <p>{t("Add the products on the note. The grid only appears after the first line.")}</p>
              </div>
              <div className="dnp-actions">
                <button
                  type="button"
                  className="dnp-btn dnp-btn--cyan"
                  onClick={addLine}
                  disabled={loading || !form.supplier_id || isCorrection}
                > {t("+ Add Line")} </button>
              </div>
            </div>

            {loading ? (
              <div className="dnp-empty">
                <strong>{t("Loading catalogues")}</strong>
                <span>{t("Suppliers, items and units are being prepared for this delivery note.")}</span>
              </div>
            ) : !lines.length ? (
              <div className="dnp-empty">
                <strong>{t("No lines yet")}</strong>
                <span>{t("Choose the supplier and then add the first item. New lines appear on top.")}</span>
              </div>
            ) : isMobile ? (
              <div className="dnp-mobile">
                {lines.map((line, index) => {
                  const item = isCorrection ? correctionUnitSource(line, itemMap.get(String(line.item_id))) : itemMap.get(String(line.item_id));
                  const unitOptions = buildDeliveryNoteUnitOptions(item, unitMap, languageId).filter((option) => !isCorrection || option.level === "unit" || option.level === line.__persisted_unit_level);
                  const inventoryValues = deliveryNoteInventoryValues(line, item, unitMap, languageId);
                  const pendingCandidates = pendingByItem.get(String(line.item_id)) || [];
                  const pendingTotal = pendingCandidates.reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                  return (
                    <article key={`delivery-note-line-${index}`} className="dnp-card">
                      <div className="dnp-cardTop">
                        <div>
                          <div className="dnp-cardTitle">{item ? t(itemLabel(item)) : t("Line {number}", { number: lines.length - index })}</div>
                          {renderLineLocation(line, index)}
                          <div className="dnp-cardSub">
                            {item ? packaging(item, unitMap, languageId) : t("Select the product captured on this delivery note.")}
                          </div>
                        </div>
                        <button type="button" className="dnp-btn dnp-btn--danger" onClick={() => removeLine(index)}> {t("Remove")} </button>
                      </div>

                      <div className="mt-3">
                        <label className="dnp-label">{t("Item")}</label>
                        <select
                          className="form-select"
                          value={line.item_id}
                          disabled={isCorrection}
                          onChange={(e) => {
                            const nextItem = itemMap.get(String(e.target.value));
                            if (nextItem) pickItem(index, nextItem);
                            else setLineField(index, "item_id", "");
                          }}
                        >
                          <option value="">{t("Select item")}</option>
                          {supplierItems.map((itemOption) => (
                            <option key={`mobile-item-${itemOption.id}`} value={itemOption.id}>
                              {t(itemLabel(itemOption))}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="dnp-inline mt-2">
                        <button type="button" className="dnp-btn dnp-btn--soft" disabled={isCorrection} onClick={() => openCreateItem(index)}> {t("+ New Item")} </button>
                      </div>

                      <div className="dnp-grid dnp-grid--two mt-3">
                        <div>
                          <label className="dnp-label">{t("Unit on Note")}</label>
                          <select
                            className="form-select"
                            value={line.unit_level || ""}
                            onChange={(event) => setLineField(index, "unit_level", event.target.value)}
                            disabled={!item}
                          >
                            {!unitOptions.length ? <option value="">{t("Unit not configured")}</option> : null}
                            {unitOptions.map((option) => (
                              <option key={`mobile-uom-${index}-${option.level}`} value={option.level}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {item ? <span className="dnp-unitRelation">{unitHierarchyLabel(item, unitMap, languageId)}</span> : null}
                          {item ? <span className="dnp-unitPreview">{deliveryNoteStockPreview(line, item, unitMap, languageId, form.document_type)}</span> : null}
                        </div>
                        <div>
                          <label className="dnp-label" title={t("Qty")}>Q</label>
                          <DeliveryNoteQuantityDetail line={line} source={item} documentType={form.document_type}><input
                            type="number"
                            step="0.00000001"
                            className="form-control"
                            value={line.quantity_in_dn}
                            placeholder="0.00"
                            onChange={(e) => setLineField(index, "quantity_in_dn", e.target.value)}
                          /></DeliveryNoteQuantityDetail>
                        </div>
                        <div>
                          <label className="dnp-label">{t("Unit Price (EUR)")}</label>
                          <ComplimentaryPriceField
                            type="number"
                            step="0.00000001"
                            className="form-control"
                            value={line.unit_price_in_dn}
                            placeholder="0.00"
                            aria-label={t("Unit Price (EUR)")}
                            isComplimentary={Boolean(line.is_free_stock)}
                            priceStatus={line.price_status}
                            priceEstimateSource={line.price_estimate_source}
                            onPriceOptions={() => editLinePrice(index)}
                            onChange={(e) => setLineField(index, "unit_price_in_dn", e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="dnp-label">{t("Expiration date")}</label>
                          <ExpirationDateField line={line} item={item} pendingItem={!item}
                            disabled={Boolean(savingMode)} onChange={(value) => setLineField(index, "expiration_date", value)} />
                        </div>
                      </div>

                      <div className="dnp-meta mt-3">
                        <span>{t("Line Total")}</span>
                        <strong>{money(line.total_price_in_dn)}</strong>
                      </div>
                      <div className="dnp-meta mt-3">
                        <span>{t("Pending PO")}</span>
                        <strong>
                          {assignmentQty(line) > 0
                            ? `Received ${qty(assignmentQty(line))} ${inventoryValues.option?.inventoryLabel || ""}`
                            : pendingTotal > 0
                              ? `${qty(pendingTotal)} pending`
                              : loadingPendingPo && line.item_id
                                ? t("Checking...")
                                : t("No pending")}
                        </strong>
                        {!loadingPendingPo && pendingTotal > 0 ? (
                          <button type="button" className="dnp-btn dnp-btn--cyan mt-2" onClick={() => openReceiveModal(index)}> {t("Receive")} </button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="dnp-shell">
                <div className="dnp-wrapTable" ref={tableRef}>
                  <table className="dnp-table">
                    <thead>
                      <tr>
                        <th style={{ width: "25%" }}>{t("Item")}</th>
                        <th style={{ width: "15%", textAlign: "center" }}>{t("Unit on Note")}</th>
                        <th style={{ width: "6%", textAlign: "center" }} title={t("Qty")}>Q</th>
                        <th style={{ width: "11%", textAlign: "center" }}>{t("Unit Price (EUR)")}</th>
                        <th style={{ width: "13%" }}>{t("Expiration date")}</th>
                        <th style={{ width: "13%", textAlign: "center" }}>{t("Pending PO")}</th>
                        <th style={{ width: "11%", textAlign: "right" }}>{t("Line Total (EUR)")}</th>
                        <th style={{ width: "6%", textAlign: "right" }}>{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => {
                        const item = isCorrection ? correctionUnitSource(line, itemMap.get(String(line.item_id))) : itemMap.get(String(line.item_id));
                        const relation = relationFor(item, selectedSupplier || form.supplier_id);
                        const supplierRef = relation?.supplier_code || relation?.code || relation?.reference;
                        const pendingCandidates = pendingByItem.get(String(line.item_id)) || [];
                        const pendingTotal = pendingCandidates.reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                        const assignedTotal = assignmentQty(line);
                        const unitOptions = buildDeliveryNoteUnitOptions(item, unitMap, languageId).filter((option) => !isCorrection || option.level === "unit" || option.level === line.__persisted_unit_level);
                        const inventoryValues = deliveryNoteInventoryValues(line, item, unitMap, languageId);
                        return (
                          <tr key={`delivery-note-table-${index}`}>
                            <td>
                              <button
                                type="button"
                                className="dnp-trigger"
                                disabled={isCorrection} onClick={() => setItemPicker({ open: true, lineIndex: index, query: "" })}
                              >
                                <span>{item ? t(itemLabel(item)) : t("Select item")}</span>
                                <span>{t("Browse")}</span>
                              </button>
                              {renderLineLocation(line, index)}
                              <div className="dnp-itemMeta">
                                {item
                                  ? [`${t("Packaging:")} ${packaging(item, unitMap, languageId)}`, supplierRef ? t("Supplier ref: {reference}", { reference: supplierRef }) : null].filter(Boolean).join(" · ")
                                  : t("Choose the item shown on the delivery note or create a new one.")}
                              </div>
                              <div className="dnp-inline">
                                <button type="button" className="dnp-link" disabled={isCorrection} onClick={() => openCreateItem(index)}> {t("+ New Item")} </button>
                              </div>
                            </td>
                            <td className="dnp-unitCell">
                              <select
                                className="form-select dnp-unitSelect"
                                value={line.unit_level || ""}
                                onChange={(event) => setLineField(index, "unit_level", event.target.value)}
                                disabled={!item}
                              >
                                {!unitOptions.length ? <option value="">{t("Unit not configured")}</option> : null}
                                {unitOptions.map((option) => (
                                  <option key={`table-uom-${index}-${option.level}`} value={option.level}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {item ? <span className="dnp-unitRelation">{unitHierarchyLabel(item, unitMap, languageId)}</span> : null}
                              {item ? <span className="dnp-unitPreview">{deliveryNoteStockPreview(line, item, unitMap, languageId, form.document_type)}</span> : null}
                            </td>
                            <td>
                              <DeliveryNoteQuantityDetail line={line} source={item} documentType={form.document_type}><input
                                type="number"
                                step="0.00000001"
                                className="form-control dnp-input"
                                value={line.quantity_in_dn}
                                placeholder="0.00"
                                onChange={(e) => setLineField(index, "quantity_in_dn", e.target.value)}
                              /></DeliveryNoteQuantityDetail>
                            </td>
                            <td>
                              <ComplimentaryPriceField
                                type="number"
                                step="0.00000001"
                                className="form-control dnp-input"
                                value={line.unit_price_in_dn}
                                placeholder="0.00"
                                aria-label={t("Unit Price (EUR)")}
                                isComplimentary={Boolean(line.is_free_stock)}
                                priceStatus={line.price_status}
                                priceEstimateSource={line.price_estimate_source}
                                onPriceOptions={() => editLinePrice(index)}
                                onChange={(e) => setLineField(index, "unit_price_in_dn", e.target.value)}
                              />
                            </td>
                            <td><ExpirationDateField line={line} item={item} pendingItem={!item}
                              disabled={Boolean(savingMode)} onChange={(value) => setLineField(index, "expiration_date", value)} /></td>
                            <td>
                              <div className="dnp-poStatus">
                                <span
                                  className={`dnp-poText ${
                                    assignedTotal > 0 ? "dnp-poText--linked" : pendingTotal > 0 ? "dnp-poText--pending" : ""
                                  }`}
                                >
                                  {assignedTotal > 0
                                    ? `Received ${qty(assignedTotal)} ${inventoryValues.option?.inventoryLabel || ""}`
                                    : pendingTotal > 0
                                      ? `${qty(pendingTotal)} pending`
                                      : loadingPendingPo && line.item_id
                                        ? t("Checking...")
                                        : t("No pending")}
                                </span>
                                {!loadingPendingPo && pendingTotal > 0 ? (
                                  <button type="button" className="dnp-btn dnp-btn--cyan" onClick={() => openReceiveModal(index)}> {t("Receive")} </button>
                                ) : null}
                              </div>
                            </td>
                            <td className="dnp-total">{money(line.total_price_in_dn)}</td>
                            <td style={{ textAlign: "right" }}>
                              <button type="button" className="dnp-btn dnp-btn--danger" onClick={() => removeLine(index)}> {t("Remove")} </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          <DeliveryNoteAdjustments variant="dnp" lines={form.lines} expenses={form.expenses} languageId={languageId}
            disabled={isCorrection || saving || loading} onChange={(nextLines, nextExpenses) => {
              setForm((previous) => ({ ...previous, lines: nextLines.map(withTotal), expenses: nextExpenses }));
              setError("");
            }} />

          <DeliveryNotePendingCorrection note={form} lines={lines} sources={lines.map((line) => itemMap.get(String(line.item_id)))} disabled={saving || loading}
            onChange={(value) => setField("correction_manual_pending", value)} onLineChange={(index, value) => setLineField(index, "correction_pending_quantity", value)} />
          {!isCorrection && quantityReview.review}

          <div className="dnp-footer mt-3">
            <div className="dnp-totalBlock">
              <span>{t(hasPendingPrices ? "Provisional total" : "Total")}</span>
              <strong>{money(total)}</strong>
            </div>
            <div className="dnp-footerActions">
              <button type="button" className="dnp-btn dnp-btn--soft" onClick={() => save()} disabled={saving || loading}>
                {savingMode === "save" ? t("Saving...") : t("Save Delivery Note")}
              </button>
              {canSaveAndVerify ? (
                <button
                  type="button"
                  className="dnp-btn dnp-btn--primary"
                  onClick={() => save({ verify: true })}
                  disabled={saving || loading}
                >
                  {savingMode === "verify" ? t("Saving and verifying...") : t("Save and Verify Delivery Note")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <PickerModal
        open={!isMobile && supplierPicker.open}
        title={t("Select Supplier")}
        subtitle={t("Choose the supplier first so the item list becomes shorter and more relevant.")}
        query={supplierPicker.query}
        onQuery={(query) => setSupplierPicker((prev) => ({ ...prev, query }))}
        onClose={() => setSupplierPicker({ open: false, query: "" })}
      >
        {filteredSuppliers.length ? (
          filteredSuppliers.map((supplier) => (
            <button
              key={`supplier-${supplier.id}`}
              type="button"
              className="dnp-pickItem"
              onClick={() => {
                setField("supplier_id", supplier.id);
                setSupplierPicker({ open: false, query: "" });
              }}
            >
              <strong>{t(supplierLabel(supplier))}</strong>
              <span>{supplier.tax_id || t("Browse this supplier's linked items")}</span>
            </button>
          ))
        ) : (
          <div className="dnp-pickEmpty">{t("No suppliers match this search.")}</div>
        )}
      </PickerModal>

      <PickerModal
        open={!isMobile && itemPicker.open}
        title={t("Select Item")}
        subtitle={selectedSupplier ? t("Showing items linked to {supplier}.", { supplier: selectedSupplier.name }) : t("Choose a supplier first.")}
        query={itemPicker.query}
        onQuery={(query) => setItemPicker((prev) => ({ ...prev, query }))}
        onClose={() => setItemPicker({ open: false, lineIndex: null, query: "" })}
      >
        {filteredItems.length ? (
          filteredItems.map((item) => {
            const relation = relationFor(item, selectedSupplier || form.supplier_id);
            const supplierRef = relation?.supplier_code || relation?.code || relation?.reference;
            return (
              <button
                key={`item-${item.id}`}
                type="button"
                className="dnp-pickItem"
                onClick={() => pickItem(itemPicker.lineIndex, item)}
              >
                <strong>{t(itemLabel(item))}</strong>
                <span>
                  {[
                    `Packaging: ${packaging(item, unitMap, languageId)}`,
                    supplierRef ? t("Supplier ref: {reference}", { reference: supplierRef }) : null,
                    t("Suggested price: {price}", { price: money(suggestedPrice(item, selectedSupplier || form.supplier_id)) }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            );
          })
        ) : (
          <div className="dnp-pickEmpty"> {t("No matching items. Create a new one if the supplier note contains something not yet in the catalogue.")} </div>
        )}
      </PickerModal>

      <ReceivePoModal
        open={receiveModal.open}
        line={receiveModal.lineIndex !== null ? lines[receiveModal.lineIndex] : null}
        item={
          receiveModal.lineIndex !== null && lines[receiveModal.lineIndex]
            ? itemMap.get(String(lines[receiveModal.lineIndex].item_id))
            : null
        }
        unitMap={unitMap}
        candidates={
          receiveModal.lineIndex !== null && lines[receiveModal.lineIndex]
            ? pendingByItem.get(String(lines[receiveModal.lineIndex].item_id)) || []
            : []
        }
        quantities={receiveModal.quantities}
        onQuantity={setReceiveQuantity}
        onClose={() => setReceiveModal({ open: false, lineIndex: null, quantities: {} })}
        onApply={applyReceiveAssignments}
      />

      {showItemPopup ? (
        <AddItemPopup
          isOpen={showItemPopup}
          context="delivery_note"
          onClose={() => {
            setShowItemPopup(false);
            setPendingLineIndex(null);
          }}
          onAdded={handleItemAdded}
        />
      ) : null}
    </div>
    {complimentary.dialog}
    </>
  );
}
